/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IAlertProps, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { CookieContext } from '@msdyn365-commerce/runtime-internal/dist/types/utils/cookie-utils';
import classnames from 'classnames';
import * as React from 'react';

import { CookieComplianceAcceptButton, CookieComplianceLinks } from './components';
import { IVinhusetCookieComplianceConfig, IVinhusetCookieComplianceProps } from './vinhuset-cookie-compliance.props.autogenerated';
import { CookieComplianceAcceptEssentialButton } from './components/accept-essential-button';
import { IVinhusetCookieComplianceData } from './vinhuset-cookie-compliance.data';

/**
 * Alert state interface.
 */
export interface IAlertState {
    visible: boolean;
}

/**
 * Cookie compliance view props interface.
 */
export interface ICookieComplianceViewProps extends IVinhusetCookieComplianceProps<{}> {
    CookieComplianceBanner: IModuleProps;
    AlertProps: IAlertProps;
    Content: INodeProps;
    ButtonContainer: INodeProps;
    text?: React.ReactNode;
    links?: React.ReactNode;
    acceptButton?: React.ReactNode;
    acceptEssentialButton?: React.ReactNode;
    className: string;
    onDismiss(): void;
}

/**
 *
 * CookieCompliance component.
 * @extends {React.PureComponent<IVinhusetCookieComplianceProps<IHeroConfig>>}
 */
class CookieCompliance extends React.PureComponent<IVinhusetCookieComplianceProps<IVinhusetCookieComplianceData>, IAlertState> {
    private readonly cookieRef: React.RefObject<HTMLInputElement>;

    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public constructor(props: IVinhusetCookieComplianceProps<IVinhusetCookieComplianceData>) {
        super(props);
        this.state = { visible: false };
        this.cookieRef = React.createRef();
        this._onDismiss = this._onDismiss.bind(this);
    }

    public componentDidMount(): void {
        const { context } = this.props;
        const cookies = context.request.cookies as CookieContext;
        const hideCookieBannerCookie = !!cookies.get('_msdyn365__hcb_', true).value;

        if (!hideCookieBannerCookie && !context.request.cookies.isConsentGiven()) {
            this.setState({ visible: true });
        }

        if (context.request.cookies.isConsentGiven()) {
            try {
                if (this.props.config.clarity && window && (window as any).clarity) {
                    try {
                        const { customerInformation, organizationInformation } = this.props.data;

                        if (customerInformation.result?.AccountNumber) {
                            (window as any).clarity('set', 'customerId', customerInformation.result.AccountNumber);
                        }

                        if (organizationInformation.result?.AccountNumber) {
                            (window as any).clarity('set', 'organizationCustomerId', organizationInformation.result.AccountNumber);
                        }
                    } catch (error) {
                        console.error('Error while setting clarity consent cookie', error);
                    }
                }
            } catch {
                // Do nothing
            }
        }
    }

    public render(): JSX.Element | null {
        const { config, resources, telemetry } = this.props;
        const {
            acceptCookiesButtonText,
            acceptCookiesAriaLabel,
            acceptEssentialCookiesButtonText,
            acceptEssentialCookiesAriaLabel
        } = resources;
        const { visible } = this.state;

        let moduleConfig: IVinhusetCookieComplianceConfig;

        try {
            moduleConfig = config;
        } catch (error) {
            telemetry.error(`Something went wrong while rendering the alert module ------${error}`);
            return <div>{error}</div>;
        }

        if (!visible) {
            return <></>;
        }

        if (!MsDyn365.msdyn365Commerce.isBrowser) {
            return <></>;
        }

        const viewProps: Partial<ICookieComplianceViewProps> = {
            ...this.props,
            // onDismiss: this._onDismiss,
            CookieComplianceBanner: {
                moduleProps: this.props,
                className: classnames('ms-cookie-compliance', moduleConfig.className),
                ref: this.cookieRef
            },
            AlertProps: {
                className: 'ms-cookie-compliance__container',
                color: 'white',
                fade: false
            },
            Content: {
                className: 'ms-cookie-compliance__content'
            },
            ButtonContainer: {
                className: 'ms-cookie-compliance__button-container'
            },
            text: moduleConfig.content && (
                <MsDyn365.RichTextComponent
                    className='ms-cookie-compliance__text'
                    text={moduleConfig.content}
                    editProps={{ onEdit: this.handleContentChange, requestContext: this.props.context.request }}
                />
            ),
            links: ArrayExtensions.hasElements(moduleConfig.actionLinks) && (
                <CookieComplianceLinks
                    links={moduleConfig.actionLinks}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                    telemetryContent={this.telemetryContent}
                />
            ),
            acceptButton: (
                <CookieComplianceAcceptButton
                    onClose={() => this._onDismiss(true)}
                    acceptCookiesButtonText={acceptCookiesButtonText}
                    acceptCookiesButtonAriaLabel={acceptCookiesAriaLabel}
                    telemetryContent={this.telemetryContent}
                />
            ),
            acceptEssentialButton: (
                <CookieComplianceAcceptEssentialButton
                    onClose={() => this._onDismiss(false)}
                    acceptEssentialCookiesButtonText={acceptEssentialCookiesButtonText}
                    acceptEssentialCookiesButtonAriaLabel={acceptEssentialCookiesAriaLabel}
                    telemetryContent={this.telemetryContent}
                />
            )
        };

        return this.props.renderView(viewProps as ICookieComplianceViewProps) as React.ReactElement;
    }

    /**
     * Handles Link content change event.
     * @param linkIndex - Index of link in linkarray.
     * @returns Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent): void => {
        if (this.props.config.actionLinks?.[linkIndex]) {
            this.props.config.actionLinks[linkIndex].linkText = event.target.value;
        }
    };

    /**
     * Handles content change event.
     * @param event - Event.
     */
    public handleContentChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.content = event.target.value;
    };

    private _onDismiss(consentAll: boolean): void {
        const { context, config } = this.props;
        const cookies = context.request.cookies as CookieContext;

        if (consentAll && cookies) {
            cookies.setConsentCookie();

            if (config.clarity && window && (window as any).clarity) {
                try {
                    const { customerInformation, organizationInformation } = this.props.data;
                    // Tell Clarity that the user has consented to all cookies
                    (window as any).clarity('consent');

                    if (customerInformation.result?.AccountNumber) {
                        (window as any).clarity('set', 'customerId', customerInformation.result.AccountNumber);
                    }

                    if (organizationInformation.result?.AccountNumber) {
                        (window as any).clarity('set', 'organizationCustomerId', organizationInformation.result.AccountNumber);
                    }
                } catch (error) {
                    console.error('Error while setting clarity consent cookie', error);
                }
            }

            location.reload();
        }

        if (!consentAll) {
            // Set cookie to hide the banner
            cookies.set('_msdyn365__hcb_', Date.now(), { path: '/', maxAge: 31536000, isEssential: true });
        }

        this.setState({ visible: false });
    }
}

export default CookieCompliance;
